/**
 * The four reports every site gets, as a static client catalogue (POS2-1137, decision 3).
 *
 * Nothing is seeded per site. `GET /reports?site_id=` returns nought to four rows, and a card with
 * no row renders the never-configured state — so "every site gets four from day one" is true by
 * construction, onboarding writes nothing, and there is no backfill to run.
 *
 * **Keys, never labels.** `check-i18n.mjs` check 6 fails a module-level map of capital-first
 * English prose in any file outside `pages/admin/`, `pages/labelling/`, `components/admin/` and
 * `i18n/`, and `lib/` is not one of them. That check exists because nineteen rendered English
 * labels sat in `WASTE_TYPE_LABELS` for a release (POS2-1176); a name resolved here would also
 * capture the locale at import time and never change again.
 *
 * **Section order is imported, not restated.** `SECTIONS_BY_REPORT` in `reports/document` is the
 * one declaration of what a report contains and in what order, shared with the backend and the
 * renderer. Note that section ids are *not* unique across reports — `itemsW` belongs to both the
 * Weekly and the Monthly — so an enabled set is only meaningful alongside its report type.
 *
 * **There is no page-count constant here, deliberately.** The mock and the PRD say Daily 1 /
 * Weekly 2 / Monthly 4 / Wins 1, and the paginator POS2-1136 shipped does not agree. Measured
 * against it, fully enabled with five item rows and a remainder: Daily 804 px, Weekly 900,
 * Monthly 945, Wins 451, against a usable page of 883 — so 1, 2, **2**, 1. A count is a fact
 * about a payload, which is why `pageCount` takes one, and a card that has not fetched a payload
 * cannot state it. The card says how the report arrives; the preview says how long it is.
 */
import { BarChart3, CalendarDays, Sun, Trophy, type LucideIcon } from "lucide-react";

// From the leaf module, never the barrel. `@/reports/document` re-exports `ReportDocument`, and
// this file is reached from the *eager* Reports page — so a barrel import here puts the whole
// document and its four layouts one tree-shake away from the main bundle, which is the thing
// decision 14's lazy route exists to keep out of it. Rollup does currently shake it out either
// way; importing the leaf makes that structural rather than a property of the bundler's analysis,
// and `report-chunk.test.ts` is what holds it.
import { SECTIONS_BY_REPORT, type SectionId } from "@/reports/document/sections";
import type { Cadence } from "@/reports/document/types";

export type ReportKey = "daily" | "weekly" | "monthly" | "wins";

export interface CatalogueEntry {
  key: ReportKey;
  /** `reports.title.*` — shared with the PDF's own header, so the card and the file agree. */
  titleKey: string;
  descriptionKey: string;
  /** Wins is a monthly report; its pill says so rather than inventing a fifth cadence word. */
  frequencyKey: string;
  icon: LucideIcon;
  sections: readonly SectionId[];
}

export const REPORT_CATALOGUE: readonly CatalogueEntry[] = [
  {
    key: "daily",
    titleKey: "reports.title.daily",
    descriptionKey: "reports.description.daily",
    frequencyKey: "reports.frequency.daily",
    icon: Sun,
    sections: SECTIONS_BY_REPORT.daily,
  },
  {
    key: "weekly",
    titleKey: "reports.title.weekly",
    descriptionKey: "reports.description.weekly",
    frequencyKey: "reports.frequency.weekly",
    icon: CalendarDays,
    sections: SECTIONS_BY_REPORT.weekly,
  },
  {
    key: "monthly",
    titleKey: "reports.title.monthly",
    descriptionKey: "reports.description.monthly",
    frequencyKey: "reports.frequency.monthly",
    icon: BarChart3,
    sections: SECTIONS_BY_REPORT.monthly,
  },
  {
    key: "wins",
    titleKey: "reports.title.wins",
    descriptionKey: "reports.description.wins",
    frequencyKey: "reports.frequency.monthly",
    icon: Trophy,
    sections: SECTIONS_BY_REPORT.wins,
  },
];

/**
 * What a section is called where a person chooses it — the editor's checkbox and the preview's
 * "What's inside" line.
 *
 * Deliberately its own set of keys rather than a reuse of the eyebrows the document prints. They
 * share their English today (`reports.eyebrow.items` is also "Where it went") and they are not the
 * same string: the eyebrow is what the section header says on the page, the label is what the
 * control says beside a checkbox, and a copy edit to one should not silently move the other.
 * `check-i18n.mjs` check 5 keeps their German in step for as long as the English does match, which
 * is the guarantee worth having.
 */
export const SECTION_LABEL_KEYS: Record<SectionId, string> = {
  moneyD: "reports.section.moneyD",
  itemsD: "reports.section.itemsD",
  photo: "reports.section.photo",
  actD: "reports.section.actD",
  moneyW: "reports.section.moneyW",
  trendW: "reports.section.trendW",
  itemsW: "reports.section.itemsW",
  working: "reports.section.working",
  actW: "reports.section.actW",
  moneyM: "reports.section.moneyM",
  trendM: "reports.section.trendM",
  break: "reports.section.break",
  winsHero: "reports.section.winsHero",
  streaks: "reports.section.streaks",
  pinup: "reports.section.pinup",
};

/** The catalogue entry for a route parameter, or null when the URL names no report we have. */
export function catalogueEntry(key: string | undefined): CatalogueEntry | null {
  return REPORT_CATALOGUE.find((e) => e.key === key) ?? null;
}

/**
 * What a never-configured report is pre-filled with when somebody opens it (decision 8).
 *
 * **The roles are the ones that exist.** The mock's list — Head Chef, Sous Chef, General Manager —
 * is not a role set this platform has; the seeded roles are `Org Admin`, `Site Manager` and
 * `Staff` (`20260328114942:21-59`), so the defaults are expressed in those or they are expressed
 * in nothing. Wins has no role filter because it is written for the whole kitchen to see.
 *
 * Pre-filled on open and **written only on save**, which is what keeps opening a card free of side
 * effects — a person can look at a report they have no intention of switching on and leave no row
 * behind them.
 */
export interface LaunchDefault {
  cadence: Cadence;
  /** Weekly only: 0 = Monday, matching `zoned-day.ts` and the column. */
  sendDow: number | null;
  /** On the hour, inside the 06:00–17:00 window the schema enforces. */
  sendHour: number;
  /** Null means the whole site team. */
  roles: readonly string[] | null;
}

export const LAUNCH_DEFAULTS: Record<ReportKey, LaunchDefault> = {
  daily: { cadence: "weekdays", sendDow: null, sendHour: 7, roles: ["Staff", "Site Manager"] },
  weekly: {
    cadence: "weekly",
    sendDow: 0,
    sendHour: 7,
    roles: ["Staff", "Site Manager", "Org Admin"],
  },
  monthly: {
    cadence: "first_of_month",
    sendDow: null,
    sendHour: 8,
    roles: ["Site Manager", "Org Admin"],
  },
  wins: { cadence: "last_friday", sendDow: null, sendHour: 15, roles: null },
};

/** A forty-person organisation must not open the editor with forty chips in it. */
export const MAX_DEFAULT_RECIPIENTS = 8;

/** The cadences each report may take — the same three shapes the column's CHECK allows. */
export const CADENCE_CHOICES: Record<ReportKey, readonly Cadence[]> = {
  daily: ["weekdays", "every_day"],
  weekly: ["weekly"],
  monthly: ["first_of_month", "last_friday"],
  wins: ["first_of_month", "last_friday"],
};

/** Send hours, on the hour, as the schema's `_on_the_hour_in_window` bounds them. */
export const SEND_HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
